/**
 * MintNFTButton Component v3.3
 *
 * Allows users to mint their WEXEL NFT for an investment.
 * User pays gas fees in SOL.
 */

import { useState, useEffect } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import { Transaction } from '@solana/web3.js'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2, Sparkles, ExternalLink, CheckCircle, Clock } from 'lucide-react'
import { api } from '../services/api'

const MINT_LOCK_MS = 15 * 60 * 1000 // 15 minutes — matches Redis MINT_SESSION_TTL on backend

function parseMintError(err: any): string {
  const msg: string = err?.message ?? err?.toString() ?? ''
  const backendMsg: string = err?.response?.data?.message ?? ''
  const statusCode: number = err?.response?.status ?? 0
  const combined = msg + ' ' + backendMsg

  if (statusCode === 429 || combined.includes('Too many'))
    return 'Too many requests. Please wait a moment and try again.'

  if (combined.includes('User rejected') || combined.includes('Transaction cancelled'))
    return 'You cancelled the transaction. You can try again when ready.'

  if (combined.includes('Wallet not connected') || combined.includes('connect'))
    return 'Please connect your wallet first.'

  if (combined.includes('insufficient lamports') || combined.includes('Insufficient SOL') || combined.includes('not enough SOL'))
    return 'Not enough SOL for minting fees. Please add at least 0.02 SOL to your wallet.'

  if (combined.includes('already been processed') || combined.includes('already processed'))
    return 'This NFT may have already been created. Please refresh the page.'

  if (combined.includes('Transaction expired before it could be submitted'))
    return 'The transaction expired before it was submitted. Please try minting again.'

  if (combined.includes('already minted') || combined.includes('NFT already'))
    return 'This NFT has already been minted.'

  if (combined.includes('mint is locked') || combined.includes('locked'))
    return 'Minting is temporarily locked. Please wait a few minutes and try again.'

  if (combined.includes('Transaction failed on chain'))
    return 'The transaction failed on the blockchain. Please try again.'

  if (combined.includes('could not be confirmed'))
    return 'Transaction was sent but could not be confirmed. Please check your wallet and refresh the page.'

  if (statusCode === 503 || combined.includes('temporarily unavailable'))
    return 'Service is temporarily unavailable. Please try again in a few minutes.'

  if (statusCode === 500 || combined.includes('Internal'))
    return 'Something went wrong on our end. Please try again or contact support.'

  if (combined.includes('Network') || combined.includes('ECONNREFUSED') || combined.includes('timeout'))
    return 'Network error. Please check your connection and try again.'

  if (backendMsg) return backendMsg

  return 'Something went wrong. Please try again.'
}

interface MintNFTButtonProps {
  investmentId: string
  vaultName: string
  usdtAmount?: number
  mintLockedUntil?: string  // ISO date from backend (Redis session)
  onSuccess?: () => void
  className?: string
}

export function MintNFTButton({
  investmentId,
  vaultName,
  usdtAmount,
  mintLockedUntil: serverLockUntil,
  onSuccess,
  className = '',
}: MintNFTButtonProps) {
  const { t } = useTranslation(['nft', 'common'])
  const { publicKey, signTransaction, connected } = useWallet()
  const [loading, setLoading] = useState(false)
  const [mintAddress, setMintAddress] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [localLockUntil, setLocalLockUntil] = useState<number | null>(null)
  const [, setTick] = useState(0)

  // Effective lock: server (Redis) takes priority over local (client-side grace)
  const serverLockMs = serverLockUntil ? new Date(serverLockUntil).getTime() : null
  const effectiveLock = serverLockMs && serverLockMs > Date.now()
    ? serverLockMs
    : localLockUntil && localLockUntil > Date.now()
      ? localLockUntil
      : null

  // Tick countdown
  useEffect(() => {
    if (!effectiveLock) return
    const id = setInterval(() => {
      if (Date.now() >= effectiveLock) {
        setLocalLockUntil(null)
        clearInterval(id)
      }
      setTick(t => t + 1)
    }, 1000)
    return () => clearInterval(id)
  }, [effectiveLock])

  const lockRemaining = effectiveLock ? Math.max(0, Math.ceil((effectiveLock - Date.now()) / 1000)) : 0
  const lockDisplay = lockRemaining > 0 ? `${Math.floor(lockRemaining / 60)}:${(lockRemaining % 60).toString().padStart(2, '0')}` : null

  const handleMint = async () => {
    if (!connected || !publicKey || !signTransaction) {
      toast.error('Please connect your wallet first.')
      return
    }

    if (effectiveLock) {
      toast.warning(`Minting is locked. Please wait ${lockDisplay}.`)
      return
    }

    setLoading(true)
    setError(null)

    try {
      // 1. Get unsigned transaction from backend
      const prepareRes = await api.prepareNFTMint(investmentId)

      if (!prepareRes.success || !prepareRes.data) {
        throw new Error(prepareRes.message || t('nft:mint.error'))
      }

      const { transaction: txBase64, mintAddress: newMintAddress } = prepareRes.data

      // 2. Deserialize transaction
      const txBuffer = Buffer.from(txBase64, 'base64')
      const tx = Transaction.from(txBuffer)

      // 3. Sign with Phantom wallet (user pays gas, signs first — Phantom-safe multi-signer flow)
      toast.info(t('nft:mint.approveTransaction'))
      const signed = await signTransaction(tx)

      // 4. Send user-signed tx to backend — backend co-signs and submits to chain
      toast.info(t('investment:modal.waitingForConfirmation'))
      const signedBase64 = Buffer.from(signed.serialize({ requireAllSignatures: false, verifySignatures: false })).toString('base64')
      const submitRes = await api.submitSignedNFTMint(investmentId, signedBase64)

      if (!submitRes.success) {
        throw new Error(submitRes.message || t('nft:mint.error'))
      }

      const signature = submitRes.data!.txSignature

      setLocalLockUntil(null)
      setMintAddress(newMintAddress)
      toast.success(t('nft:mint.success'))
      onSuccess?.()
    } catch (err: any) {
      console.error('Mint error:', err)
      const errorMessage = parseMintError(err)

      // If backend returned lockExpiresIn (429), set server timer immediately
      const serverLockSec = err?.response?.data?.data?.lockExpiresIn
      if (typeof serverLockSec === 'number' && serverLockSec > 0) {
        setLocalLockUntil(Date.now() + serverLockSec * 1000)
        setError(null)
        toast.warning(`Minting is locked. Please wait ${Math.floor(serverLockSec / 60)}:${(serverLockSec % 60).toString().padStart(2, '0')}.`)
        return
      }

      // Handle "already processed" — NFT was likely created on blockchain
      if (errorMessage.includes('already been created') || errorMessage.includes('refresh the page')) {
        toast.warning(errorMessage)
        setError(errorMessage)
        setLocalLockUntil(null)
        onSuccess?.()
        return
      }

      // User cancelled or transaction expired — trigger refetch to get server lock state
      if (errorMessage.includes('cancelled') || errorMessage.includes('transaction expired')) {
        setLocalLockUntil(null)
        onSuccess?.() // triggers queryClient.invalidateQueries → refetches mintLockedUntil from server
      }

      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Already minted state
  if (mintAddress) {
    const network = import.meta.env.VITE_SOLANA_NETWORK || 'mainnet-beta'
    const solscanUrl = `https://solscan.io/token/${mintAddress}${network === 'devnet' ? '?cluster=devnet' : ''}`

    return (
      <div className={`bg-green-500/10 border border-green-500/30 rounded-lg p-4 ${className}`}>
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle className="h-5 w-5 text-green-400" />
          <span className="text-green-400 font-medium">{t('nft:mint.success')}</span>
        </div>
        <a
          href={solscanUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-gold-400 text-sm hover:underline"
        >
          View on Solscan
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    )
  }

  const isLocked = !!lockDisplay && !loading
  const isDisabled = loading || !connected || isLocked

  return (
    <div className={className}>
      <button
        onClick={handleMint}
        disabled={isDisabled}
        className={`
          bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700
          text-black font-bold py-3 px-6 rounded-lg
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-center gap-2 w-full
          transition-all duration-200
        `}
      >
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>{t('nft:mint.processing')}</span>
          </>
        ) : isLocked ? (
          <>
            <Clock className="h-5 w-5" />
            <span>Mint locked — {lockDisplay}</span>
          </>
        ) : (
          <>
            <Sparkles className="h-5 w-5" />
            <span>{t('nft:mint.button')}</span>
          </>
        )}
      </button>

      {!connected && (
        <p className="text-xs text-gray-400 mt-2 text-center">
          Please connect your wallet to mint your NFT
        </p>
      )}

      {isLocked && !loading && (
        <p className="text-xs text-yellow-400 mt-2 text-center">
          Minting is temporarily locked. Please wait for the timer to expire.
        </p>
      )}

      {error && (
        <p className="text-xs text-red-400 mt-2 text-center">
          {error}
        </p>
      )}

      {usdtAmount && (
        <p className="text-xs text-gray-400 mt-2 text-center">
          {vaultName} - ${usdtAmount.toLocaleString()} USDT
        </p>
      )}
    </div>
  )
}

export default MintNFTButton
